import { api } from '@/utils/api';
import { Task, CreateTaskData, UpdateTaskData, FilterOptions, PaginatedResponse } from '@/types';

/**
 * Task Service
 * Handles all task-related API calls
 */
class TaskService {
  private readonly BASE_URL = '/tasks';

  /**
   * Get all tasks with optional filters
   */
  async getTasks(filters?: FilterOptions): Promise<Task[]> {
    try {
      const params = this.buildQueryParams(filters);
      const response = await api.get<any>(this.BASE_URL, { params });
      return response.data || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch tasks');
    }
  }

  /**
   * Get paginated tasks
   */
  async getTasksPaginated(
    page = 1,
    limit = 20,
    filters?: FilterOptions
  ): Promise<PaginatedResponse<Task>> {
    try {
      const params = {
        ...this.buildQueryParams(filters),
        page,
        limit,
      };
      const response = await api.get<any>(this.BASE_URL, { params });
      return {
        data: response.data || [],
        pagination: {
          page: response.meta?.page || page,
          limit: response.meta?.limit || limit,
          total: response.meta?.total || 0,
          totalPages: response.meta?.totalPages || 1,
        },
      } as PaginatedResponse<Task>;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch tasks');
    }
  }

  /**
   * Get single task by ID
   */
  async getTaskById(taskId: string): Promise<Task> {
    try {
      const response = await api.get<any>(`${this.BASE_URL}/${taskId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch task');
    }
  }

  /**
   * Create new task
   */
  async createTask(taskData: CreateTaskData): Promise<Task> {
    try {
      const response = await api.post<any>(this.BASE_URL, taskData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create task');
    }
  }

  /**
   * Update existing task
   */
  async updateTask(taskId: string, taskData: UpdateTaskData): Promise<Task> {
    try {
      const response = await api.put<any>(`${this.BASE_URL}/${taskId}`, taskData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update task');
    }
  }

  /**
   * Delete task
   */
  async deleteTask(taskId: string): Promise<void> {
    try {
      await api.delete(`${this.BASE_URL}/${taskId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete task');
    }
  }

  /**
   * Get tasks by project
   */
  async getTasksByProject(projectId: string, filters?: FilterOptions): Promise<Task[]> {
    try {
      const params = this.buildQueryParams({ ...filters, projectId });
      const response = await api.get<Task[]>(this.BASE_URL, { params });
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch project tasks');
    }
  }

  /**
   * Get tasks assigned to user
   */
  async getMyTasks(filters?: FilterOptions): Promise<Task[]> {
    try {
      const params = this.buildQueryParams(filters);
      const response = await api.get<Task[]>(`${this.BASE_URL}/my-tasks`, { params });
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user tasks');
    }
  }

  /**
   * Assign task to user
   */
  async assignTask(taskId: string, userId: string): Promise<Task> {
    try {
      const response = await api.post<Task>(`${this.BASE_URL}/${taskId}/assign`, { userId });
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to assign task');
    }
  }

  /**
   * Unassign task
   */
  async unassignTask(taskId: string): Promise<Task> {
    try {
      const response = await api.post<Task>(`${this.BASE_URL}/${taskId}/unassign`);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to unassign task');
    }
  }

  /**
   * Add subtask
   */
  async addSubtask(parentTaskId: string, subtaskData: CreateTaskData): Promise<Task> {
    try {
      const response = await api.post<Task>(
        `${this.BASE_URL}/${parentTaskId}/subtasks`,
        subtaskData
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add subtask');
    }
  }

  /**
   * Get subtasks
   */
  async getSubtasks(parentTaskId: string): Promise<Task[]> {
    try {
      const response = await api.get<Task[]>(`${this.BASE_URL}/${parentTaskId}/subtasks`);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subtasks');
    }
  }

  /**
   * Upload attachment
   */
  async uploadAttachment(taskId: string, file: File): Promise<void> {
    try {
      await api.upload(`${this.BASE_URL}/${taskId}/attachments`, file);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload attachment');
    }
  }

  /**
   * Build query parameters from filters
   */
  private buildQueryParams(filters?: FilterOptions): Record<string, any> {
    if (!filters) return {};

    const params: Record<string, any> = {};

    if (filters.status && filters.status.length > 0) {
      params.status = filters.status.join(',');
    }
    if (filters.priority && filters.priority.length > 0) {
      params.priority = filters.priority.join(',');
    }
    if (filters.assigneeId && filters.assigneeId.length > 0) {
      params.assigneeId = filters.assigneeId.join(',');
    }
    if (filters.projectId) {
      params.projectId = filters.projectId;
    }
    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }
    if (filters.sortOrder) {
      params.sortOrder = filters.sortOrder;
    }

    return params;
  }
}

// Export singleton instance
export const taskService = new TaskService();
